import pick from 'lodash/pick';
import moment from 'moment';

import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {AbsoluteDate, BaseDate} from 'common/widgets/date-range-picker/models/types';
import {parseDate} from 'common/query-builder/cohorts-query-builder/util/index';
import {
  isArithmeticOperatorNode,
  isBehaviorOperandNode,
  isLiteralOperandNode,
} from 'common/query-builder/cohorts-query-builder/util/expression-tree';
import {hasUserFilters} from 'common/util/custom_events';

import {Behavior, BehaviorsDictionary} from '../behaviors/behavior';
import {BehaviorCount} from '../behaviors/behavior-count';
import {BehaviorWindow} from '../behaviors/behavior-window';
import {EventSelector} from '../behaviors/event-selector';
import {FunnelStep} from '../behaviors/funnel-step';
import {DEFAULT_EVENT, DEFAULT_FREQUENCY_VALUE, DEFAULT_RELATIVE_NUM_DAYS} from '../constants';
import {DateRangeType, FrequencyOperator} from '../enums/builder-enums';
import {ArithmeticOperator, BehaviorCountType, LogicalOperator, Property, WindowUnit} from '../enums/expression-enums';
import {OperandNode} from '../expression/operand-node';
import {OperatorNode} from '../expression/operator-node';
import {expressionToClauseRoot} from '../expression/utils';
import type {
  FrequencyClauseBuilderState,
  StagedBehaviorDateRange,
  StagedPropertyFilterClause,
} from '../types/builder-state-types';
import type {EventFrequency, Operator} from '../types/expression-types';
import type {SerializedEventFrequencyClause} from '../types/serialized-staged-cohort-types';
import {Clause} from './clause';
import {PropertyFilter} from './property-filter';

interface EventFrequencyClauseOptions {
  behavior: Behavior;
  builderState?: FrequencyClauseBuilderState;
  frequency: EventFrequency;
  frequencyOperator: FrequencyOperator;
  propertyFilterOperator?: LogicalOperator;
  propertyFilters?: PropertyFilter[];
  stagedBehaviorDateRange?: StagedBehaviorDateRange;
  stagedPeoplePropertyFilter?: PropertyFilter;
}

export class EventFrequencyClause extends Clause {
  public static deserialize(options: SerializedEventFrequencyClause): EventFrequencyClause {
    return new EventFrequencyClause({
      ...pick(options, [
        `frequency`,
        `frequencyOperator`,
        `builderState`,
        `propertyFilterOperator`,
        `stagedBehaviorDateRange`,
      ]),
      ...{stagedPeoplePropertyFilter: PropertyFilter.deserialize(options.stagedPeoplePropertyFilter)},
      ...{
        propertyFilters: options.propertyFilters.map((serializedPropClause) =>
          PropertyFilter.deserialize(serializedPropClause),
        ),
      },
      ...{behavior: Behavior.parseBehavior(options.behavior)},
    });
  }

  public static createDefault(frequency: EventFrequency = [DEFAULT_FREQUENCY_VALUE]): EventFrequencyClause {
    const defaultBehavior = new Behavior(
      new BehaviorCount(new EventSelector(DEFAULT_EVENT), BehaviorCountType.Absolute),
      null,
      null,
      null,
      null,
      new BehaviorWindow(WindowUnit.Day, DEFAULT_RELATIVE_NUM_DAYS),
    );
    return new EventFrequencyClause({
      behavior: defaultBehavior,
      frequency,
      frequencyOperator: FrequencyOperator.AtLeast,
    });
  }

  /**
   * We construct each step in the funnel as a non-funnel event frequency clause to facilitate
   * switching between funnels & other operations. From the builder's perspective, the only difference
   * between funnels and other multi-clause event literals is the clause operator.
   * @param step - the funnel step from which the value & operator will be determined
   * @param funnelBehavior - the behavior from which the window will be determined
   */
  public static createFromFunnelStep(step: FunnelStep, funnelBehavior: Behavior): EventFrequencyClause {
    const {fromDate, toDate, behaviorWindow} = funnelBehavior;
    const frequencyOperator = step.value > 0 ? FrequencyOperator.AtLeast : FrequencyOperator.EqualTo;
    const behavior = new Behavior(step.count, null, null, fromDate, toDate, behaviorWindow);

    const selectorTree = step.count.eventSelector.selector;
    const {propertyFilters, propertyFilterOperator} = EventFrequencyClause.getPropertyFiltersAndOperator(selectorTree);
    return new EventFrequencyClause({
      behavior,
      frequency: [step.value],
      frequencyOperator,
      propertyFilterOperator,
      propertyFilters,
    });
  }

  public static createFromParts({
    tree,
    behaviors,
  }: {
    tree: OperatorNode;
    behaviors: BehaviorsDictionary;
  }): EventFrequencyClause {
    const behaviorKey = tree.find<string, OperandNode>((node) => node.operand.value as string, isBehaviorOperandNode);
    const behavior = behaviors[behaviorKey];

    const frequency = tree.traverse<number, OperandNode>(
      (node) => node.operand.value as number,
      isLiteralOperandNode,
    ) as EventFrequency;
    const operators = tree.traverse<ArithmeticOperator, OperatorNode>(
      (node) => node.operator as ArithmeticOperator,
      isArithmeticOperatorNode,
    );

    let frequencyOperator;
    if (operators.length > 1) {
      frequencyOperator = FrequencyOperator.Between;
    } else {
      switch (operators[0]) {
        case ArithmeticOperator.GreaterThanEquals:
          frequencyOperator = FrequencyOperator.AtLeast;
          break;
        case ArithmeticOperator.GreaterThan:
          frequencyOperator = FrequencyOperator.GreaterThan;
          break;
        case ArithmeticOperator.Equals:
          frequencyOperator = FrequencyOperator.EqualTo;
          break;
        case ArithmeticOperator.LessThan:
          frequencyOperator = FrequencyOperator.LessThan;
          break;
        default:
          throw new Error(`unexpected frequency operator`);
      }
    }
    let propertyFilters = [];
    let propertyFilterOperator = null;
    if (behavior.count) {
      const selectorTree = behavior.count.eventSelector.selector;
      const filtersAndOperator = EventFrequencyClause.getPropertyFiltersAndOperator(selectorTree);
      propertyFilters = filtersAndOperator.propertyFilters;
      propertyFilterOperator = filtersAndOperator.propertyFilterOperator;
    }
    return new EventFrequencyClause({
      behavior,
      frequency,
      frequencyOperator,
      propertyFilterOperator,
      propertyFilters,
    });
  }

  private static isValidDate(date?: BaseDate): boolean {
    if (!!date && DateRange.isRelativeDate(date)) {
      const relativeDate = date;
      return Object.prototype.hasOwnProperty.call(relativeDate, `unit`) && relativeDate.value >= 0;
    }
    return !!parseDate(date as AbsoluteDate);
  }

  private static getNumberOfDays(fromDate?: BaseDate, toDate?: BaseDate): number {
    const [fromCalendarDate, toCalendarDate] = [fromDate, toDate].map(DateRange.convertBaseDateToCalendarDate);
    return moment(toCalendarDate).diff(moment(fromCalendarDate), `days`);
  }

  public readonly behavior: Behavior;
  public readonly frequencyOperator: FrequencyOperator;
  public readonly frequency: EventFrequency;
  public readonly stagedBehaviorDateRange: StagedBehaviorDateRange;
  // a property filter that is only ever staged, never committed. its purpose is to allow user to stage people properties on event clauses.
  // when the user commits the filter, the literal will convert from an event literal to a people literal.
  public readonly stagedPeoplePropertyFilter: PropertyFilter;
  public readonly builderState: FrequencyClauseBuilderState = [
    {
      initialInputWidth: 0,
      inputText: ``,
      isEditing: false,
    },
    {
      initialInputWidth: 0,
      inputText: ``,
      isEditing: false,
    },
  ];

  constructor(options: EventFrequencyClauseOptions) {
    super(options);
    if (options.builderState) {
      this.builderState = options.builderState;
    }

    if (options.propertyFilters) {
      const propertyFilters = options.propertyFilters.filter((clause) => clause.property);
      this.behavior = this.behavior.setEventSelector(propertyFilters, options.propertyFilterOperator);
    }

    if (this.behavior && !options.stagedBehaviorDateRange) {
      this.stagedBehaviorDateRange = this.behavior.getDateRangeOptions();
    }

    if (!options.stagedPeoplePropertyFilter) {
      this.stagedPeoplePropertyFilter = new PropertyFilter();
    }
  }

  public serialize(): SerializedEventFrequencyClause {
    return {
      ...pick(this, [
        `frequency`,
        `frequencyOperator`,
        `builderState`,
        `propertyFilterOperator`,
        `stagedBehaviorDateRange`,
      ]),
      ...{stagedPeoplePropertyFilter: this.stagedPeoplePropertyFilter.serialize()},
      ...{propertyFilters: this.getPropertyFilters().map((propertyFilter) => propertyFilter.serialize())},
      ...{behavior: this.behavior.serialize()},
    };
  }

  public isNegated(): boolean {
    return this.frequency[0] <= 0;
  }

  public addPropertyFilter(): EventFrequencyClause {
    return this.newClause({propertyFilters: this.getPropertyFilters().concat(new PropertyFilter())});
  }

  // @override
  public commitStagedFilterClause(propertyFilterIdx: number): Clause {
    const newClause = super.commitStagedFilterClause(propertyFilterIdx) as EventFrequencyClause;
    return newClause.setBehavior(
      newClause.behavior.setEventSelector(newClause.getPropertyFilters(), this.propertyFilterOperator),
    );
  }

  // @override
  public togglePropertyFilterOperator(): Clause {
    const newClause = super.togglePropertyFilterOperator() as EventFrequencyClause;
    return newClause.setBehavior(
      newClause.behavior.setEventSelector(this.getPropertyFilters(), newClause.propertyFilterOperator),
    );
  }

  // @override
  public getStagedFilterClause(propertyFilterIdx: number | null): StagedPropertyFilterClause {
    if (propertyFilterIdx !== null) {
      return super.getStagedFilterClause(propertyFilterIdx);
    }

    return this.stagedPeoplePropertyFilter.getStagedFilterClause();
  }

  // @override
  public setStagedFilterClause(options: Partial<StagedPropertyFilterClause>, propertyFilterIdx: number | null): Clause {
    if (propertyFilterIdx !== null) {
      return super.setStagedFilterClause(options, propertyFilterIdx);
    }

    // user is editing people property on top-level clause, not existing or new property filter
    return this.newClause({stagedPeoplePropertyFilter: this.stagedPeoplePropertyFilter.setStagedFilterClause(options)});
  }

  public setBehavior(behavior: Behavior): EventFrequencyClause {
    return this.newClause({behavior});
  }

  public setFrequencyOperator(frequencyOperator: FrequencyOperator): EventFrequencyClause {
    let frequency = this.frequency;
    if (frequencyOperator === FrequencyOperator.Between && frequency.length === 1) {
      frequency = [frequency[0], frequency[0] + 2]; // add two to the second frequency so that the between makes more sense
    }
    return this.newClause({frequency, frequencyOperator});
  }

  public setFrequency(frequency: EventFrequency): EventFrequencyClause {
    return this.newClause({frequency});
  }

  public setNegated(negated: boolean): EventFrequencyClause {
    if (this.isNegated() === negated) {
      return this;
    }

    const frequency = negated ? 0 : DEFAULT_FREQUENCY_VALUE;
    const frequencyOperator = negated ? FrequencyOperator.EqualTo : FrequencyOperator.AtLeast;
    return this.setFrequencyOperator(frequencyOperator).setFrequency([frequency]);
  }

  public setBuilderState(builderState: FrequencyClauseBuilderState): EventFrequencyClause {
    return this.newClause({builderState});
  }

  public setStagedBehaviorDateRange(stagedBehaviorDateRange: StagedBehaviorDateRange): EventFrequencyClause {
    return this.newClause({stagedBehaviorDateRange});
  }

  public hasValidStagedDateRange(): boolean {
    let valid = true;
    const {customRangeType, fromDate, toDate, value} = this.stagedBehaviorDateRange;
    if (customRangeType === DateRangeType.Relative) {
      valid = !Number.isNaN(parseInt(value as any, 10)) && value >= 1;
    } else if (customRangeType === DateRangeType.Between) {
      valid = EventFrequencyClause.isValidDate(fromDate) && EventFrequencyClause.isValidDate(toDate);
    } else {
      valid = EventFrequencyClause.isValidDate(fromDate);
    }
    return valid;
  }

  public getExpressionTree(): OperatorNode {
    let operatorNode;
    const behaviorNode = new OperandNode({property: Property.Behaviors, value: this.behavior.key});
    if (this.frequencyOperator === FrequencyOperator.Between) {
      const lowFrequency = Math.min(...this.frequency);
      const highFrequency = Math.max(...this.frequency);
      operatorNode = new OperatorNode(
        LogicalOperator.And,
        new OperatorNode(
          ArithmeticOperator.GreaterThanEquals,
          behaviorNode,
          new OperandNode({property: Property.Literal, value: lowFrequency}),
        ),
        new OperatorNode(
          ArithmeticOperator.LessThanEquals,
          behaviorNode,
          new OperandNode({property: Property.Literal, value: highFrequency}),
        ),
      );
    } else {
      operatorNode = new OperatorNode(
        this.getOperatorFromFrequencyOperator(this.frequencyOperator),
        behaviorNode,
        new OperandNode({property: Property.Literal, value: this.frequency[0]}),
      );
    }

    return expressionToClauseRoot(operatorNode);
  }

  public toTrackingData(): Obj {
    const {fromDate, toDate, behaviorWindow} = this.behavior;
    return {
      'clause count type': this.behavior.count.type,
      'clause date range': behaviorWindow ? behaviorWindow.toDateRangeString() : `Custom`,
      'clause days': behaviorWindow
        ? behaviorWindow.getNumDays()
        : EventFrequencyClause.getNumberOfDays(fromDate, toDate),
      'clause event': this.behavior.count.eventSelector.event,
      'clause frequency': this.frequency,
      'clause property filter operator': this.propertyFilterOperator,
      'clause property filters': this.getPropertyFilters().length,
    };
  }

  public isInvalidForDataGroup({dataGroupId, customEvents}): boolean {
    if (!dataGroupId) {
      // Always valid for User data group
      return false;
    }

    // checks for custom events with user filters
    const customEventMatch = /^\$custom_event:(\d+)/.exec(this.behavior?.count?.eventSelector?.event);
    if (customEventMatch) {
      const customEventIdString = customEventMatch[1];
      const customEvent = customEvents.find((ce) => `${ce.id}` === customEventIdString);
      return hasUserFilters(customEvent, customEvents);
    } else {
      return false;
    }
  }

  protected newClause(options: Partial<EventFrequencyClauseOptions> = {}): EventFrequencyClause {
    const oldOptions: EventFrequencyClauseOptions = {
      behavior: this.behavior,
      builderState: this.builderState,
      frequency: this.frequency,
      frequencyOperator: this.frequencyOperator,
      propertyFilterOperator: this.propertyFilterOperator,
      propertyFilters: this.propertyFilters,
    };
    return new EventFrequencyClause({...oldOptions, ...options});
  }

  private getOperatorFromFrequencyOperator(frequencyOperator: FrequencyOperator): Operator {
    switch (frequencyOperator) {
      case FrequencyOperator.AtLeast:
        return ArithmeticOperator.GreaterThanEquals;
      case FrequencyOperator.GreaterThan:
        return ArithmeticOperator.GreaterThan;
      case FrequencyOperator.EqualTo:
        return ArithmeticOperator.Equals;
      case FrequencyOperator.LessThan:
        return ArithmeticOperator.LessThan;
      default:
        throw new Error(`Invalid frequency operator: ${frequencyOperator}`);
    }
  }
}
